import React from "react";
import ProfileContainer from "./ProfileContainer";
import {
  Box,
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Dialog,
  DialogActions,
  Chip,
  CircularProgress,
} from "@mui/material";
import useNotificationHistoryStore from "../zstore/investorNotificationHistory";
import { useShallow } from "zustand/react/shallow";
import Text from "../components/Text";
import { formatDate } from "../utils/date";
import { NavArrowRight } from "iconoir-react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  LoadingButton,
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import useBoolean from "../hooks/useBoolean";
import useConfirmDialog from "../hooks/useConfirmDialog";
import useInvestorEvidenceRequestStore from "../zstore/investorEvidenceRequest";
import { EvidenceRequestStatus } from "../constant/requiredEvidence";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import { useSelector } from "react-redux";
import { getEntityTypeCheckers } from "../utils/investor";
import { selectFromAmlStore } from "../amlSlice";
import {
  NotificationEditionPayload,
  NotificationHistoryItem,
  NotificationHistoryStatus,
} from "../api/transformers/notification";
import useMessage from "../hooks/useMessage";
import EmailPreviewEditor, {
  EmailPreviewEditorRef,
} from "./EmailPreviewEditor";
import { AmlStatus } from "../constant/amlConstant";
import { AmlAPI, DocAPI, NotificationAPI, RqdEvidenceAPI } from "../api";
import useRequiredEvidenceTypesStore from "../zstore/requiredEvidenceTypes";
import {
  base64ToUtf8,
  HTMLProcessHandler,
  preprocessHTML,
  utf8ToBase64,
} from "../utils/htmlHelpers";
import Empty from "../components/Empty";
import { usePermissionAction } from "../hooks/usePermissionAction";
import { InvestorEvidenceRequestOverview } from "../api/transformers/requiredEvidence";

type NotificationProfileProps = {
  personId: string;
};

type FormikValue = {
  type?: NotificationTypeEnum;
  notificationText?: string;
  agreed?: boolean;
};

const NotificationType = {
  DEFAULT: "default",
  CUSTOMIZED: "customized",
  NOT_SEND_EMAIL: "notSendEmail",
} as const;

type NotificationTypeEnum =
  (typeof NotificationType)[keyof typeof NotificationType];

const validationSchema = yup.object({
  type: yup.string().required("This Field is Required."),
  notificationText: yup.string().when("type", {
    is: (t: NotificationTypeEnum) => t === NotificationType.CUSTOMIZED,
    then: (schema) => schema.required("Please enter email notification text."),
    otherwise: (schema) => schema.notRequired(),
  }),
  agreed: yup.boolean().when("type", {
    is: (t: NotificationTypeEnum) => t === NotificationType.CUSTOMIZED,
    then: (schema) => schema.oneOf([true], "Please Confirm."),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const entityTypeSelector = selectFromAmlStore((state) => state.entityTypes);

const NotificationProfile: React.FC<NotificationProfileProps> = ({
  personId,
}) => {
  const checkAmlUpdatePermission = usePermissionAction("update:investor-aml");
  const {
    notificationHistory,
    latestNotification,
    fetchNotificationHistory,
    notificationHistoryLoading,
  } = useNotificationHistoryStore(
    useShallow((state) => ({
      notificationHistory: state.data,
      latestNotification: state.getLatestNotification(),
      fetchNotificationHistory: state.fetchData,
      notificationHistoryLoading: state.loading,
    }))
  );
  const entityTypes = useSelector(entityTypeSelector);
  const entityTypeCheckers = getEntityTypeCheckers(entityTypes);
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const message = useMessage();
  const formik = useFormik<FormikValue>({
    initialValues: {
      type: NotificationType.DEFAULT,
      notificationText: "",
      agreed: false,
    },
    validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });
  const [submitting, { setTrue: startSubmitting, setFalse: finishSubmitting }] =
    useBoolean();
  const [
    loadingEmailPreview,
    { setTrue: startLoadingEmailPreview, setFalse: finishLoadingEmailPreview },
  ] = useBoolean();
  const [
    previewModalOpen,
    { setTrue: showPreviewModal, setFalse: closePreviewModal },
  ] = useBoolean();
  const [csgNotificationInfo, setCsgNotificationInfo] = React.useState<
    Partial<NotificationEditionPayload>
  >({});
  const [emailHTML, setEmailHTML] = React.useState("");
  const {
    data: requiredEvidenceTypes,
    loading: requiredEvidenceLoading,
    error: requiredEvidenceError,
    getType,
    getAmlCategoriesOfType,
    getAmlCategory,
  } = useRequiredEvidenceTypesStore(personId);
  const {
    data: evidenceRequests,
    loading: evidenceRequestsLoading,
    fetchData: refetchEvidenceRequests,
  } = useInvestorEvidenceRequestStore(
    useShallow((state) => ({
      data: state.data,
      loading: state.loading,
      fetchData: state.fetchData,
    }))
  );
  const {
    data: amlDetail,
    loading: amlDetailLoading,
    fetchData: refetchAmlDetail,
  } = useInvestorAmlDetailStore();
  const [notificationHistoryModalOpen, setNotificationHistoryModalOpen] =
    React.useState(false);
  const hasPendingEvidenceRequest = evidenceRequests.some((el) =>
    [
      EvidenceRequestStatus.REQ,
      EvidenceRequestStatus.NOT,
      EvidenceRequestStatus.FRQ,
    ].includes(el.status as any)
  );
  const isInvestorIndividual = amlDetailLoading
    ? false
    : entityTypeCheckers.isIndividual(amlDetail?.entityType);
  // enable default notification if there's any pending evidence request and the investor is of individual type.
  const defaultNotificationEnabled =
    hasPendingEvidenceRequest && isInvestorIndividual;
  const loading =
    amlDetailLoading || evidenceRequestsLoading || notificationHistoryLoading;
  const hasRequiredEvidenceRequest = evidenceRequests.some((el) => el.status === EvidenceRequestStatus.REQ);
  const requestEvidenceButtonEnable =
    formik.values.type === NotificationType.NOT_SEND_EMAIL
      ? amlDetail?.status !== AmlStatus.EVI || hasRequiredEvidenceRequest
      : true;

  const handleShowCancelPrompt = () => {
    confirm({
      content: "Cancel and lose the entered text?",
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        formik.setFieldValue("notificationText", "");
      },
    });
  };
  const handleShowNotificationTimeline = () => {
    setNotificationHistoryModalOpen(true);
    fetchNotificationHistory(personId, true);
  };
  const handleSubmit = async (values: FormikValue) => {
    // [HWAP-5385] Restrict Notifications while AML status are Submitted.
    if (amlDetail?.status === AmlStatus.SUB) {
      message.info({
        content:
          "Please wait for the risk assessment to complete before requesting any required reference.",
        style: {
          marginTop: "45vh",
        },
      });
      return;
    }
    if (values.type === NotificationType.NOT_SEND_EMAIL) {
      if (!requestEvidenceButtonEnable) {
        message.info({
          content:
            "The AML Status or the Required Evidence Status have been modified and do not need to be resubmitted.",
          style: {
            marginTop: "45vh",
          },
        });
        return;
      }
      startSubmitting();
      refetchEvidenceRequests(personId, requiredEvidenceTypes);
      Promise.all([updateAmlStatus(), ...updateRequiredEvidenceStatus()])
        .then(() => {
          message.success("Submission successful!");
          refetchAmlDetail(personId);
          refetchEvidenceRequests(personId, requiredEvidenceTypes);
        })
        .catch((err) => {
          console.error(err);
          message.error("Error! We couldn't submit your form.");
        })
        .finally(finishSubmitting);
    } else {
      confirm({
        title: "Please Choose",
        content:
          "Send the notification now or would you like to preview the content first?",
        okText: "Send",
        cancelText: "Preview",
        onOk: () => {
          sendNotificationDirectly(values);
        },
        onCancel: () => {
          return getPreviewNotification(values);
        },
      });
    }
  };

  const updateAmlStatus = () => {
    const payload = {
      sendNotification: false,
      status: AmlStatus.EVI,
    };
    return AmlAPI.updateStatus(personId, payload);
  };

  const updateRequiredEvidenceStatus = () => {
    return evidenceRequests.map((er: InvestorEvidenceRequestOverview) => {
      if (er.status === EvidenceRequestStatus.REQ) {
        const payload = {
          evidenceStatus: EvidenceRequestStatus.NOT,
          evidenceTypeCode: er.code,
          reviewerComment: "",
          submitterComment: "",
          documents: [],
        };
        return RqdEvidenceAPI.updateEvidenceRequest(personId, payload);
      }
    });
  };

  const getPreviewNotification = async (values: FormikValue) => {
    startLoadingEmailPreview();
    try {
      const notificationInfo = await NotificationAPI.previewNotification(
        personId,
        {
          type: values.type!,
          notificationText:
            values.type === NotificationType.CUSTOMIZED
              ? values.notificationText
              : undefined,
        }
      );
      if (notificationInfo) {
        const htmlStr = base64ToUtf8(notificationInfo.subNarrative_base64);
        setEmailHTML(preprocessHTML(htmlStr, htmlPreprocessHandlers));
        setCsgNotificationInfo({
          notfnUrn: notificationInfo.notfnUrn,
          subject: notificationInfo.subject,
          triggerId: notificationInfo.triggerId,
        });
        showPreviewModal();
      }
      finishLoadingEmailPreview();
    } catch (err) {
      console.error(err);
      finishLoadingEmailPreview();
    }
  };

  const handleConfirmNotificationEdit = (newEmailBody?: string) => {
    if (!newEmailBody) {
      return;
    }
    startSubmitting();
    closePreviewModal();
    NotificationAPI.confirmNotificationEdition(personId, {
      status: "READY",
      subNarrative_base64: utf8ToBase64(newEmailBody),
      subject: csgNotificationInfo.subject!,
      notfnUrn: csgNotificationInfo.notfnUrn!,
      triggerId: csgNotificationInfo.triggerId!,
    })
      .then(() => {
        message.success("Submission successful! Notification sent.");
        fetchNotificationHistory(personId);
        refetchAmlDetail(personId);
        refetchEvidenceRequests(personId, requiredEvidenceTypes);
        formik.setValues(
          {
            type: formik.values.type,
            notificationText: "",
            agreed: false,
          },
          false
        );
      })
      .catch((err) => {
        console.error(err);
        message.error(
          "Error! We couldn't submit your form or send the notification."
        );
      })
      .finally(finishSubmitting);
  };

  const sendNotificationDirectly = (values: FormikValue) => {
    startSubmitting();
    NotificationAPI.sendNotification(personId, {
      type: values.type!,
      notificationText:
        values.type === NotificationType.CUSTOMIZED
          ? values.notificationText
          : undefined,
    })
      .then(() => {
        message.success("Submission successful! Notification sent.");
        fetchNotificationHistory(personId);
        refetchAmlDetail(personId);
        refetchEvidenceRequests(personId, requiredEvidenceTypes);
        formik.setValues(
          {
            type: values.type,
            notificationText: "",
            agreed: false,
          },
          false
        );
      })
      .catch((err) => {
        console.error(err);
        message.error(
          "Error! We couldn't submit your form or send the notification."
        );
      })
      .finally(finishSubmitting);
  };
  return (
    <ProfileContainer id="notification-section" title="Request Evidence">
      <Text sx={{ fontWeight: "bold" }}>
        Select a notification option
      </Text>
      <Grid container>
        <Grid item xs={7.5}>
          <FormControl
            error={formik.touched.type && Boolean(formik.errors.type)}
          >
            <RadioGroup
              name="type"
              value={formik.values.type}
              onChange={formik.handleChange}
            >
              <FormControlLabel
                value={NotificationType.NOT_SEND_EMAIL}
                disabled={submitting}
                label="Do not send an email"
                control={<Radio size="small" />}
              />
              <FormControlLabel
                value={NotificationType.DEFAULT}
                disabled={submitting || !defaultNotificationEnabled}
                label="Send the default email"
                control={<Radio size="small" />}
              />
              <FormControlLabel
                value={NotificationType.CUSTOMIZED}
                disabled={submitting}
                label="Send an email containing custom text"
                control={<Radio size="small" />}
              />
            </RadioGroup>
            {formik.touched.type && formik.errors.type && (
              <FormHelperText>{formik.errors.type}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={4.5}>
          <Stack direction="column" style={{ width: "100%" }} rowGap="8px">
            <Text>
              Date Last Email Sent:{" "}
              {latestNotification
                ? formatDate(latestNotification.sentDate)
                : "-"}
            </Text>
            <Link
              onClick={handleShowNotificationTimeline}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                textDecoration: "none",
                width: "fit-content",
                cursor: "pointer",
              }}
            >
              Notification History <NavArrowRight />
            </Link>
          </Stack>
        </Grid>
      </Grid>
      {formik.values.type === NotificationType.CUSTOMIZED && (
        <Stack direction="column" rowGap="10px">
          <Text sx={{ fontWeight: "bold" }}>
            * Enter the email notification text (or paste from a template)
          </Text>
          <TextField
            name="notificationText"
            multiline
            disabled={submitting}
            rows={4}
            value={formik.values.notificationText}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            error={
              formik.touched.notificationText &&
              Boolean(formik.errors.notificationText)
            }
            helperText={
              formik.touched.notificationText && formik.errors.notificationText
            }
          />
        </Stack>
      )}
      {formik.values.type === NotificationType.CUSTOMIZED && (
        <FormControl
          error={formik.touched.agreed && Boolean(formik.errors.agreed)}
          component="fieldset"
          variant="standard"
          sx={{ marginTop: "8px" }}
        >
          <FormControlLabel
            labelPlacement="end"
            control={
              <Checkbox
                name="agreed"
                checked={formik.values.agreed}
                onChange={(e) => {
                  formik.setFieldValue("agreed", e.target.checked);
                }}
              />
            }
            label="I confirm that the text I have entered for the customer email contains no information to personally identify the subject in line with GDPR directives."
          />
          {formik.touched.agreed && formik.errors.agreed && (
            <FormHelperText sx={{ marginLeft: "9px" }} error>
              {formik.errors.agreed}
            </FormHelperText>
          )}
        </FormControl>
      )}
      <Stack
        direction="row"
        justifyContent="flex-end"
        rowGap="10px"
        sx={{ marginTop: "8px" }}
      >
        <LoadingButton
          variant="contained"
          color="primary"
          sx={{ borderRadius: "4px" }}
          onClick={checkAmlUpdatePermission(formik.submitForm)}
          loading={loadingEmailPreview || submitting}
        >
          Request Evidence
        </LoadingButton>
        {formik.values.type === NotificationType.CUSTOMIZED && (
          <Button
            variant="text"
            sx={{
              backgroundColor: "transparent",
              color: "primary.main",
              padding: "6px 8px",
              minWidth: "64px",
              fontSize: "0.875rem",
              fontWeight: 500,
              lineHeight: 1.75,
              borderRadius: "4px",
              textTransform: "uppercase",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                boxShadow: "none",
              },
            }}
            onClick={checkAmlUpdatePermission(handleShowCancelPrompt)}
          >
            Cancel
          </Button>
        )}
      </Stack>
      {ConfirmDialog}
      <NotificationHistoryModal
        history={notificationHistory}
        open={notificationHistoryModalOpen}
        onClose={() => setNotificationHistoryModalOpen(false)}
      />
      <NotificationPreviewModal
        open={previewModalOpen}
        value={emailHTML}
        onCancel={closePreviewModal}
        onSend={handleConfirmNotificationEdit}
      />
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size="1rem" />
        </Box>
      )}
    </ProfileContainer>
  );
};

const HistoryItemConfig: Record<
  NotificationHistoryStatus,
  { color: string; description: string }
> = {
  PENDING: {
    color: "blue",
    description: "SENDING",
  },
  FAILED: {
    color: "red",
    description: "FAILED",
  },
  SENT: {
    color: "green",
    description: "SENT",
  },
};
type NotificationHistoryModalProps = {
  open: boolean;
  history: NotificationHistoryItem[];
  onClose: () => void;
};
const NotificationHistoryModal: React.FC<NotificationHistoryModalProps> = ({
  open,
  history,
  onClose,
}) => {
  const message = useMessage();
  const renderTimelineContent = (item: NotificationHistoryItem) => {
    const config = HistoryItemConfig[item.status as NotificationHistoryStatus];
    const handleViewEmail = (urn: string) => {
      DocAPI.getLink(urn)
        .then((link) => {
          window.open(link, "_blank", "width=800,height=600");
        })
        .catch((error) => {
          console.error(error);
          message.error("Failed to fetch email.");
        });
    };
    return (
      <Stack
        direction="column"
        rowGap="4px"
        sx={{
          border: (theme) =>
            theme.palette.mode === "light"
              ? "1px solid #f0f0f0"
              : "1px solid #2a2a2a",
          borderRadius: "8px",
          padding: "12px",
          backgroundColor: (theme) =>
            theme.palette.mode === "light" ? "#fff" : "#121212",
          width: "100%",
        }}
      >
        <Text sx={{ fontSize: "12px" }}>{formatDate(item.sentDate)}</Text>
        <Chip
          sx={{
            width: "fit-content",
            color: "#fff",
            borderRadius: "4px",
            backgroundColor: config?.color,
          }}
          label={config?.description}
        />
        {item.description && <Text>{item.description}</Text>}
        {item.fileURN && (
          <Link
            sx={{ cursor: "pointer" }}
            onClick={() => handleViewEmail(item.fileURN!)}
          >
            View Email
          </Link>
        )}
      </Stack>
    );
  };
  return (
    <Dialog
      open={open}
      fullWidth
      PaperProps={{
        sx: {
          p: 0,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1.2rem" }}>
        Notification Timeline
      </DialogTitle>
      <DialogContent sx={{ maxHeight: "500px" }}>
        {history.length === 0 ? (
          <Empty />
        ) : (
          <Timeline
            sx={{
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {history.map((el, index) => {
              const isLast = history.length - 1 === index;
              return (
                <TimelineItem
                  key={`${el.sentDate}-${index}`} // <-- add unique key
                >
                  <TimelineSeparator>
                    <TimelineDot />
                    {isLast ? null : <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>{renderTimelineContent(el)}</TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

type NotificationPreviewModalProps = {
  open: boolean;
  value: string;
  onCancel: () => void;
  onSend: (content?: string) => void;
};
const NotificationPreviewModal: React.FC<NotificationPreviewModalProps> = ({
  open,
  value,
  onCancel,
  onSend,
}) => {
  const quillRef = React.useRef<EmailPreviewEditorRef | null>(null);
  React.useEffect(() => {
    if (open) {
      quillRef.current?.setEmailHTMLContent(value);
    }
  }, [open]);
  const handleOk = () => {
    onSend && onSend(quillRef.current?.getEmailHTMLContent());
  };
  const handleCancel = () => {
    onCancel && onCancel();
  };
  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          p: 0,
          width: "85%",
          height: "550px",
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1.2rem" }}>
        Notification Preview
      </DialogTitle>
      <DialogContent>
        <EmailPreviewEditor
          ref={quillRef}
          defaultValue={value}
          style={{ height: "400px", paddingBottom: "45px" }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          sx={{
            backgroundColor: "transparent",
            color: "primary.main",
            padding: "6px 8px",
            minWidth: "64px",
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: 1.75,
            borderRadius: "4px",
            textTransform: "uppercase",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.04)",
              boxShadow: "none",
            },
          }}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          sx={{ borderRadius: "4px" }}
          onClick={handleOk}
        >
          Send
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

const htmlPreprocessHandlers: HTMLProcessHandler[] = [
  {
    // <font color="red">test</font> -> <span style="color: red">test</span>
    predicate: (el) => el.tagName.toLowerCase() === "font",
    handler: (el, _, { changeTagName }) => {
      const newEl = changeTagName(el, "span");
      if (newEl.hasAttribute("color")) {
        const color = newEl.getAttribute("color");
        const oldStyle = newEl.getAttribute("style") || "";
        newEl.setAttribute("style", oldStyle + `;color: ${color}`);
      }
    },
  },
  {
    // <pre>test</pre> -> <p>test</p>
    predicate: (el) => el.tagName.toLowerCase() === "pre",
    handler: (el, _, { changeTagName }) => {
      const newEl = changeTagName(el, "p");
      return newEl;
    },
  },
  {
    // remove empty div
    predicate: (el) => {
      const isEmptyDiv =
        el.tagName.toLowerCase() === "div" && el.innerHTML.trim() === "";
      return isEmptyDiv;
    },
    handler: () => {
      return null;
    },
  },
  {
    // remove empty p
    predicate: (el) => {
      const isEmptyP =
        el.tagName.toLowerCase() === "p" && el.innerHTML.trim() === "";
      return isEmptyP;
    },
    handler: () => {
      return null;
    },
  },
];

export default NotificationProfile;
